import jwtDecode from 'jwt-decode';
import { DOMAIN, KeycloakInfo } from '@/util/env.js';
import { setToken } from '@/util/jwt-cache.js';
import { loginToDomain } from '@/repository/auth/account.js';
import Keycloak from 'keycloak-js';
import * as LocalStorage from '@/repository/localStorage.js';

const keycloak = new Keycloak(KeycloakInfo);

let initPromise;
let isInit = false;

const state = {
	main: {
		token: null,
		expires: 0,
		account: null
	},
	ssoToken: null,
	ssoIdToken: null,
	ssoRefreshToken: null,
	ssoParsed: null
};

const getters = {

	account: (state) => {
		return state.main.account;
	},

	isLoggedIn: (state) => {
		let ts = ~~(Date.now()/1000);
		return state.main.expires > ts
			&& state.main.token !== null;
	},

	currentToken: (state) => {
		return state.main.token;
	},

	hasAccountSelected: (state) => {
		return state.main.account !== null;
	},

	getValidTokenForAccount: (state) => (accountId) => {
		let ts = ~~(Date.now()/1000);
		if (state.main.account === accountId && state.main.expires > ts) {
			return state.main.token;
		}
		return null;
	},

	hasSsoLogin: (state) => {
		return state.ssoParsed && state.ssoParsed.exp > Date.now()/1000;
	},

	ssoProfileLink: () => keycloak.createAccountUrl(),

	ssoToken: (state) => state.ssoToken,

	ssoUserId: (state) => {
		return state.ssoParsed?.sub;
	}
};

const actions = {

	/**
	 * should be called once at the start
	 **/
	async initApp({ dispatch, state, commit }) {
		if (!initPromise) {
			let opts = {
				onLoad: 'check-sso',
				checkLoginIframe: false,
				enableLogging: true,
				idToken: '',
				token: '',
				refreshToken: '',
			};
			if (state.ssoToken && state.ssoRefreshToken) {
				opts.token = state.ssoToken;
				opts.idToken = state.ssoIdToken;
				opts.refreshToken = state.ssoRefreshToken;
			}
			keycloak.onAuthError = err => {
				console.error('onAuthError', err, new Date());
			}
			keycloak.onAuthRefreshError = err => {
				console.error('onAuthRefreshError', err, new Date());
			}
			keycloak.onTokenExpired = () => {
				console.warn('onTokenExpired', new Date());
			}
			// console.log('keycloak init', opts);
			initPromise = keycloak.init(opts);
		}
		let auth = false;
		try {
			auth = await initPromise;
		} catch (err) {
			console.warn(err);
			commit('logout');
		}
		if (isInit) return;
		isInit = true;
		setToken(() => dispatch('getOrCreateZdbToken'));
		console.log('init', auth, keycloak);
		if (auth) {
			if (keycloak.refreshToken) {
				try {
					await keycloak.updateToken(65);
				} catch (e) {
					//refresh token expired
					console.log('refresh failed', e);
					return;
				}
			}
			await dispatch('authenticate');
		} else {
			commit('logout');
		}
	},

	/**
	 * call to log in the user
	 **/
	async authenticate({ commit, dispatch, state, rootState, rootGetters }) {
		// console.log('authenticate', keycloak);
		if (!keycloak.token) {
			let locale = rootState.base.uiLocale.substr(0, 2);
			let country = rootGetters['country/currentCountry'];
			const idpHint = new URL(document.location.href).searchParams.get('idpHint') || country.data.ssoIdpHint;
			let params = {
				redirectUri: document.location.href,
				locale,
				idpHint
			};
			let url = await keycloak.createLoginUrl(params);
			window.location.href = url;
			return;
		}
		commit('setSsoToken', keycloak);
		let data = await loginToDomain(state.ssoToken, state.main.account);
		commit('login', data.jwt);
		await dispatch('auth/user/switchToFirstAvailableAccount', null, { root: true });
	},

	async register({ rootState, rootGetters }) {
		let locale = rootState.base.uiLocale.substr(0, 2);
		let country = rootGetters['country/currentCountry'];
		let params = {
			redirectUri: document.location.href,
			locale,
			idpHint: country.data.ssoIdpHint
		};
		let url = await keycloak.createRegisterUrl(params);
		window.location.href = url;
	},

	/**
	 * call to log out the user
	 **/
	async logout({ commit }) {
		commit('logout');
		await keycloak.logout();
	},

	/**
	 * switch zdb accounts
	 **/
	async switchAccountAndLogin({ commit, dispatch, getters, state, rootGetters }, accountId) {
		// errors in this function are never displayed to user? not translated for now
		if (!accountId) throw new Error('no accountId specified');
		if (accountId === state.main.account) return;
		dispatch('redirect/runHook', { trigger: 'afterAccountSwitch' }, { root: true });
		let existing = getters.getValidTokenForAccount(accountId);
		if (existing) {
			commit('login', existing);
		} else {
			await dispatch('getSsoToken');
			let data = await loginToDomain(state.ssoToken, accountId);
			if (!data) throw new Error('Could not switch account.');
			commit('login', data.jwt);
		}
		let country = rootGetters['country/currentCountry'].id;
		LocalStorage.setLastUsedAccountId(country, accountId);
		await dispatch('auth/user/loadAccountData', null, { root: true });
	},

	getSsoCredentialsLink() {
		return (
			keycloak.createAccountUrl({
				redirectUri: document.location.href,
			}) + '#/credentials'
		);
	},

	ssoIsValid({ state }) {
		return !!(state.ssoToken && (state.ssoParsed?.exp || 0) > Math.floor(Date.now() / 1000));
	},

	async getSsoToken({ dispatch, commit, state }) {
		let valid = await dispatch('ssoIsValid');
		if (!valid && state.ssoRefreshToken) {
			try {
				await keycloak.updateToken(10);
				commit('setSsoToken', keycloak);
			} catch (err) {	//most likely because not logged in on sso
				console.warn('token refresh failed', err);
				commit('logout');
				return null;
			}
		}
		return state.ssoToken;
	},

	async getOrCreateZdbToken({ state, dispatch, commit }) {
		// console.debug('getOrCreateZdbToken', state.main.expires - now);
		if (!state.ssoRefreshToken) return;
		if (!await dispatch('getSsoToken')) return;
		const now = Math.floor(Date.now() / 1000);
		if (await dispatch('ssoIsValid') && (!state.main.expires || state.main.expires < now)) {
			let accountId = state.main.account;
			console.debug('getting fresh token for account', accountId);
			if (!accountId) return undefined;
			const data = await loginToDomain(state.ssoToken, accountId);
			if (!data) throw new Error('Could not switch account.');
			commit('login', data.jwt);
		}
		return state.main.token;
	},
};

const mutations = {

	login(state, jwt) {
		// errors in this function are never displayed to user? not translated for now
		let { aud, sub, exp } = jwtDecode(jwt);
		if (aud !== DOMAIN) {
			throw new Error(`token expected for ${DOMAIN} but received for ${aud}`);
		}
		let expDate = new Date(exp * 1000);
		if (expDate < new Date()) {
			throw new Error('token is expired');
		}
		if (!sub) sub = null;
		state.main.token = jwt;
		state.main.expires = exp;
		state.main.account = sub;
	},

	logout(state) {
		// console.log(new Error('logout').stack);
		state.main.token = null;
		state.main.account = null;
		state.main.expires = 0;
		state.ssoToken = null;
		state.ssoParsed = null;
		state.ssoIdToken = null;
		state.ssoRefreshToken = null;
	},

	setSsoToken(state, { token, idToken, tokenParsed, refreshToken }) {
		if (!token) console.error('no token on keycloak instance');
		else state.ssoToken = token;
		if (!tokenParsed) console.error('no parsed token found');
		else state.ssoParsed = tokenParsed;
		if (!refreshToken) console.error('no refresh token on keycloak instance');
		else state.ssoRefreshToken = refreshToken;
		if (!idToken) console.error('no idToken on keycloak');
		else state.ssoIdToken = idToken;
	}
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations
};
